import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import appointmentService from '../../../services/appointmentService';
import { sendNotaryAppointmentEmail } from '../../../services/resendEmailService';
import { scheduleAppointmentReminders } from '../../../services/appointmentReminderService';

const AppointmentModal = ({ notary, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    appointment_type: 'Real Estate Transaction',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    location: '',
    client_notes: '',
    property_reference: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const appointmentTypes = [
    'Real Estate Transaction',
    'Property Transfer',
    'Legal Consultation',
    'Document Verification',
    'Contract Review',
    'Inheritance Matter',
    'Other'
  ];

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear field error on change
    if (fieldErrors?.[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData?.client_name?.trim()) errors.client_name = 'Le nom complet est requis';
    if (!formData?.client_email?.trim()) {
      errors.client_email = "L'adresse email est requise";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.client_email)) {
      errors.client_email = 'Adresse email invalide';
    }
    if (!formData?.scheduled_date) errors.scheduled_date = 'La date est requise';
    if (!formData?.scheduled_time) errors.scheduled_time = "L'heure est requise";
    return errors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors)?.length > 0) {
      setFieldErrors(errors);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setFieldErrors({});

      // Combine date and time
      const scheduledDateTime = new Date(`${formData?.scheduled_date}T${formData?.scheduled_time}:00`);

      const appointmentData = {
        notary_id: notary?.id,
        appointment_type: formData?.appointment_type,
        scheduled_date: scheduledDateTime?.toISOString(),
        duration_minutes: parseInt(formData?.duration_minutes),
        location: formData?.location || notary?.office_address,
        client_notes: formData?.client_notes,
        status: 'scheduled'
      };

      if (formData?.property_reference) {
        appointmentData.notes = `Référence propriété: ${formData?.property_reference}`;
      }

      const { data, error: createError } = await appointmentService?.createAppointment(appointmentData);

      if (createError) {
        setError(createError);
        return;
      }

      if (formData?.client_email) {
        sendNotaryAppointmentEmail({
          client_name: formData?.client_name,
          client_email: formData?.client_email,
          client_phone: formData?.client_phone,
          notary_name: notary?.notary_name || notary?.user_profile?.full_name,
          notary_office: notary?.office_address,
          appointment_type: formData?.appointment_type,
          scheduled_date: new Date(`${formData?.scheduled_date}T${formData?.scheduled_time}:00`)?.toISOString(),
          scheduled_time: formData?.scheduled_time,
          duration_minutes: formData?.duration_minutes,
          property_reference: formData?.property_reference,
        });
      }

      if (data?.id) {
        scheduleAppointmentReminders({
          appointment_id: data?.id,
          client_name: formData?.client_name,
          client_email: formData?.client_email,
          client_phone: formData?.client_phone,
          appointment_type: formData?.appointment_type,
          scheduled_date: new Date(`${formData?.scheduled_date}T${formData?.scheduled_time}:00`)?.toISOString(),
          scheduled_time: formData?.scheduled_time,
          notary_name: notary?.notary_name || notary?.user_profile?.full_name,
          location: formData?.location || notary?.office_address,
        });
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
      }, 2000);

    } catch (err) {
      setError(err?.message || 'Erreur lors de la création du rendez-vous');
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today?.toISOString()?.split('T')?.[0];
  };

  // Helper: field class with error state
  const fieldClass = (fieldName, extra = '') =>
    `w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent transition-colors ${
      fieldErrors?.[fieldName]
        ? 'border-red-400 focus:ring-red-400 bg-red-50/30' :'border-border focus:ring-brand-gold'
    } ${extra}`;

  // Helper: inline error message
  const FieldError = ({ name }) =>
    fieldErrors?.[name] ? (
      <p className="mt-1 flex items-center gap-1 text-xs text-red-600">
        <Icon name="AlertCircle" size={12} color="currentColor" />
        {fieldErrors?.[name]}
      </p>
    ) : null;

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-2xl shadow-ios-large max-w-md w-full mx-4 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-text-primary mb-2">
            Rendez-vous Confirmé !
          </h2>
          <p className="text-ios-callout text-text-secondary mb-6">
            Votre demande de rendez-vous a été envoyée. Le notaire vous contactera pour confirmer.
          </p>
          <Button variant="default" fullWidth onClick={onSuccess}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-ios-large max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-ios-title2 font-display font-display-semibold text-text-primary">
                Prendre Rendez-vous
              </h2>
              <p className="text-ios-callout text-text-secondary">
                avec {notary?.user_profile?.full_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface/70 rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="client_name"
                  value={formData?.client_name}
                  onChange={handleInputChange}
                  className={fieldClass('client_name')}
                  placeholder="Votre nom complet"
                />
                <FieldError name="client_name" />
              </div>
              
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="client_email"
                  value={formData?.client_email}
                  onChange={handleInputChange}
                  className={fieldClass('client_email')}
                  placeholder="votre.email@exemple.com"
                />
                <FieldError name="client_email" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  name="client_phone"
                  value={formData?.client_phone}
                  onChange={handleInputChange}
                  className={fieldClass('client_phone')}
                  placeholder="+225 XX XX XX XX XX"
                />
              </div>

              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Type de service <span className="text-red-500">*</span>
                </label>
                <select
                  name="appointment_type"
                  value={formData?.appointment_type}
                  onChange={handleInputChange}
                  className={fieldClass('appointment_type')}
                >
                  {appointmentTypes?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Date souhaitée <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="scheduled_date"
                  value={formData?.scheduled_date}
                  onChange={handleInputChange}
                  min={getMinDate()}
                  className={fieldClass('scheduled_date')}
                />
                <FieldError name="scheduled_date" />
              </div>

              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Heure souhaitée <span className="text-red-500">*</span>
                </label>
                <select
                  name="scheduled_time"
                  value={formData?.scheduled_time}
                  onChange={handleInputChange}
                  className={fieldClass('scheduled_time')}
                >
                  <option value="">Sélectionnez une heure</option>
                  {timeSlots?.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
                <FieldError name="scheduled_time" />
              </div>

              <div>
                <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                  Durée (min)
                </label>
                <select
                  name="duration_minutes"
                  value={formData?.duration_minutes}
                  onChange={handleInputChange}
                  className={fieldClass('duration_minutes')}
                >
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 heure</option>
                  <option value={90}>1h30</option>
                  <option value={120}>2 heures</option>
                </select>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                Lieu du rendez-vous
              </label>
              <input
                type="text"
                name="location"
                value={formData?.location}
                onChange={handleInputChange}
                placeholder={`Par défaut: ${notary?.office_address}`}
                className={fieldClass('location')}
              />
            </div>

            <div>
              <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                Référence de propriété (optionnel)
              </label>
              <input
                type="text"
                name="property_reference"
                value={formData?.property_reference}
                onChange={handleInputChange}
                className={fieldClass('property_reference')}
                placeholder="Ex: REF-PROP-001"
              />
            </div>

            <div>
              <label className="block text-ios-callout font-system font-body-medium text-text-primary mb-2">
                Message (optionnel)
              </label>
              <textarea
                name="client_notes"
                value={formData?.client_notes}
                onChange={handleInputChange}
                rows="4"
                className={`${fieldClass('client_notes')} resize-none`}
                placeholder="Décrivez brièvement l'objet de votre demande ou vos questions..."
              ></textarea>
            </div>

            {/* Field errors summary */}
            {Object.keys(fieldErrors)?.length > 0 && (
              <div className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                <Icon name="AlertCircle" size={16} color="currentColor" className="mt-0.5 flex-shrink-0" />
                <span>Veuillez corriger les champs en rouge avant de continuer.</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex space-x-3 pt-4">
              <Button 
                type="button"
                variant="outline" 
                fullWidth 
                onClick={onClose}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button 
                type="submit"
                variant="default" 
                fullWidth 
                loading={loading}
                disabled={loading}
              >
                {loading ? 'Envoi en cours...' : 'Confirmer le rendez-vous'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal;